import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment-timezone";
import { getDestinationTimeZone } from "./dateHelper";
import { getDestinationDateTime, parseDestinationClock } from "./destinationDateTime";

/** Interpret typed clock times in the destination zone, and project stored instants into it. */
export function createDestinationDateAdapter(timeZone: string) {
    const zone = getDestinationTimeZone(timeZone);
    return class DestinationDateAdapter extends AdapterMoment {
        constructor(options?: ConstructorParameters<typeof AdapterMoment>[0]) {
            super(options);
            this.date = value => {
                if (value === null) return null;
                const date =
                    typeof value === "string" ? moment.tz(value, zone) : moment(value).tz(zone);
                return this.locale ? date.locale(this.locale) : date;
            };
            this.parse = (value, format) =>
                value === ""
                    ? null
                    : parseDestinationClock(value, format, this.getCurrentLocaleCode(), zone);
            this.parseISO = value => this.date(value)!;
            const wallFormat = "YYYY-MM-DDTHH:mm:ss.SSS";
            const applyWall = (date: moment.Moment, wall: moment.Moment) =>
                getDestinationDateTime(
                    parseDestinationClock(
                        wall.format(wallFormat),
                        wallFormat,
                        this.getCurrentLocaleCode(),
                        zone,
                    ),
                    date,
                    zone,
                );
            const setWall = (date: moment.Moment, unit: moment.unitOfTime.All, count: number) => {
                if (!date.isValid()) return moment.invalid();
                const wall = moment.utc(date.clone().tz(zone).format(wallFormat)).set(unit, count);
                return applyWall(date, wall);
            };
            this.setHours = (date, count) => setWall(date, "hour", count);
            this.setMinutes = (date, count) => setWall(date, "minute", count);
            this.setSeconds = (date, count) => setWall(date, "second", count);
            this.setDate = (date, count) => setWall(date, "date", count);
            this.setMonth = (date, count) => setWall(date, "month", count);
            this.setYear = (date, count) => setWall(date, "year", count);
            this.mergeDateAndTime = (date, time) => {
                if (!date.isValid() || !time.isValid()) return moment.invalid();
                const clock = time.clone().tz(zone);
                const wall = moment.utc(date.clone().tz(zone).format(wallFormat)).set({
                    hour: clock.hour(),
                    minute: clock.minute(),
                    second: clock.second(),
                    millisecond: clock.millisecond(),
                });
                return applyWall(date, wall);
            };
        }
    };
}
